import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { ApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';

import { WishlistList } from '@notino/shared/definitions/types';

import messages from '../messages';

export const getSingleProductNotificationMessage = ({
  addedToLists,
  removedFromLists,
  productName,
}: {
  addedToLists: { name: string }[];
  removedFromLists: { name: string }[];
  productName: string;
}) => {
  if (addedToLists.length > 0 && removedFromLists.length > 0) {
    return messages.listsWereUpdated;
  }
  if (addedToLists.length > 1) {
    return {
      ...messages.productAddedToLists,
      values: { product: productName },
    };
  }
  if (addedToLists.length === 1) {
    return {
      ...messages.productAddedToList,
      values: {
        product: productName,
        list: addedToLists[0].name,
      },
    };
  }
  if (removedFromLists.length > 1) {
    return {
      ...messages.productRemovedFromLists,
      values: {
        product: productName,
      },
    };
  }
  if (removedFromLists.length === 1) {
    return {
      ...messages.productRemovedFromList,
      values: {
        product: productName,
        list: removedFromLists[0].name,
      },
    };
  }
  return messages.listsWereUpdated; // This should never happen
};

export const getMultipleProductsNotificationMessage = ({
  addedToLists,
}: {
  addedToLists: { name: string }[];
}) => {
  if (addedToLists.length === 1) {
    return {
      ...messages.productsMovedToList,
      values: {
        list: addedToLists[0].name,
      },
    } as const;
  }
  return messages.productsMovedToLists;
};

export const getWishlistErrorToastProps = (
  error: ApolloError | GraphQLError,
  lists: WishlistList[]
) => {
  const errDetail =
    error instanceof GraphQLError
      ? error.extensions
      : error.graphQLErrors?.[0]?.extensions;

  if (errDetail?.type === 'urn:notino:wishlist:error:too-many-lists') {
    return {
      icon: 'IconSolidError' as const,
      content: (
        <FormattedMessage
          {...messages.tooManyListsError}
          values={{
            b: (chunks) => <b>{chunks}</b>,
            maxLists: errDetail.maximumLists as number,
          }}
        />
      ),
    };
  }

  if (
    errDetail?.type === 'urn:notino:wishlist:error:too-many-products-in-list'
  ) {
    return {
      icon: 'IconSolidError' as const,
      content: (
        <FormattedMessage
          {...messages.tooManyProductsInListError}
          values={{
            b: (chunks) => <b>{chunks}</b>,
            maxProducts: errDetail.maximumProducts as number,
            listName: lists.find((l) => l.id === errDetail.listId)?.name || '',
          }}
        />
      ),
    };
  }

  return null;
};
